import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { UserCog, UserPlus, KeyRound, Ban, CheckCircle2, Trash2, Search, ShieldCheck, Loader2 } from "lucide-react";

const ROLES = [
  ["super_admin", "Super Admin"], ["staff", "Staff"], ["society_admin", "Society Admin"],
  ["accountant", "Accountant"], ["society_member", "Resident"], ["vendor", "Vendor"],
  ["expert", "Expert"], ["unassigned", "Unassigned"],
];
const ROLE_LABEL = Object.fromEntries(ROLES);
const ROLE_TONE = {
  super_admin: "bg-primary/10 text-primary", staff: "bg-sky-100 text-sky-700",
  society_admin: "bg-emerald-100 text-emerald-700", accountant: "bg-teal-100 text-teal-700",
  society_member: "bg-amber-100 text-amber-700", vendor: "bg-orange-100 text-orange-700",
  expert: "bg-violet-100 text-violet-700", unassigned: "bg-muted text-muted-foreground",
};
const CREATABLE = ROLES.filter(([k]) => k !== "unassigned");
const empty = { name: "", email: "", password: "", role: "staff", society_id: "" };

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [data, setData] = useState({ users: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [edit, setEdit] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (role) params.set("role", role);
    if (q) params.set("q", q);
    api.get(`/admin/access/users?${params.toString()}`).then((r) => setData(r.data))
      .catch(() => toast.error("Could not load users")).finally(() => setLoading(false));
  }, [role, q]);

  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);

  const err = (e, f) => toast.error(e.response?.data?.detail || f);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/access/users", { ...form, society_id: form.society_id || undefined });
      toast.success("Login created"); setCreateOpen(false); setForm(empty); load();
    } catch (e) { err(e, "Could not create login"); } finally { setBusy(false); }
  };
  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/access/users/${edit.user_id}`, { name: edit.name, role: edit.role, society_id: edit.society_id || null });
      toast.success("Updated"); setEdit(null); load();
    } catch (e) { err(e, "Could not update"); } finally { setBusy(false); }
  };
  const resetPw = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/access/users/${pwUser.user_id}/password`, { password: pw });
      toast.success("Password reset — the user must log in again"); setPwUser(null); setPw("");
    } catch (e) { err(e, "Could not reset password"); } finally { setBusy(false); }
  };
  const toggleDisabled = async (u) => {
    try { await api.patch(`/admin/access/users/${u.user_id}`, { disabled: !u.disabled });
      toast.success(u.disabled ? "Access restored" : "Login disabled"); load();
    } catch (e) { err(e, "Could not update access"); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete login for ${u.email}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/access/users/${u.user_id}`); toast.success("Login deleted"); load(); }
    catch (e) { err(e, "Could not delete"); }
  };

  const isMe = (u) => u.user_id === me?.user_id;
  const isOwner = (u) => u.email === me?.email && me?.role === "super_admin";

  return (
    <div data-testid="admin-users-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2"><UserCog className="w-6 h-6 text-primary" /> Users & Access</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage every login on the platform — staff, society admins, residents, accountants, vendors and experts.</p>
        </div>
        <Button onClick={() => { setForm(empty); setCreateOpen(true); }} className="rounded-full gap-2" data-testid="add-login-btn"><UserPlus className="w-4 h-4" /> Add Login</Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="user-role-chips">
        <button onClick={() => setRole("")} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${role === "" ? "bg-primary text-white border-primary" : "bg-background hover:bg-muted"}`} data-testid="role-chip-all">All <span className="opacity-60">({data.total})</span></button>
        {ROLES.map(([k, l]) => (data.counts[k] ? (
          <button key={k} onClick={() => setRole(k)} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${role === k ? "bg-primary text-white border-primary" : "bg-background hover:bg-muted"}`} data-testid={`role-chip-${k}`}>
            {l} <span className="opacity-60">({data.counts[k]})</span>
          </button>
        ) : null))}
      </div>

      <div className="relative max-w-sm mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="user-search" />
      </div>

      <div className="bg-card border rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left"><tr>
            <th className="p-3 font-semibold">Name</th><th className="p-3 font-semibold">Email</th>
            <th className="p-3 font-semibold">Role</th><th className="p-3 font-semibold">Linked</th>
            <th className="p-3 font-semibold">Status</th><th className="p-3 font-semibold text-right">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="p-8 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              : data.users.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No users found.</td></tr>
                : data.users.map((u) => {
                  const rk = u.role || "unassigned";
                  return (
                    <tr key={u.user_id} className="border-t hover:bg-muted/30" data-testid={`user-row-${u.user_id}`}>
                      <td className="p-3 font-medium">{u.name} {isOwner(u) && <ShieldCheck className="w-3.5 h-3.5 inline text-primary ml-1" title="Platform owner" />}</td>
                      <td className="p-3 text-muted-foreground">{u.email}</td>
                      <td className="p-3"><span className={`text-[11px] rounded-full px-2 py-0.5 ${ROLE_TONE[rk]}`}>{ROLE_LABEL[rk]}</span></td>
                      <td className="p-3 text-xs text-muted-foreground">{u.society_name || (u.vendor_id ? "Vendor profile" : "—")}</td>
                      <td className="p-3">{u.disabled ? <span className="text-[11px] rounded-full px-2 py-0.5 bg-red-100 text-red-700">Disabled</span> : <span className="text-[11px] rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700">Active</span>}</td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" className="h-8 px-2" title="Edit" onClick={() => setEdit({ ...u, role: rk, society_id: u.society_id || "" })} data-testid={`user-edit-${u.user_id}`}><UserCog className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" className="h-8 px-2" title="Reset password" onClick={() => { setPwUser(u); setPw(""); }} data-testid={`user-pw-${u.user_id}`}><KeyRound className="w-4 h-4" /></Button>
                          {!isMe(u) && !isOwner(u) && (
                            <Button size="sm" variant="ghost" className={`h-8 px-2 ${u.disabled ? "text-emerald-600" : "text-amber-600"}`} title={u.disabled ? "Enable" : "Disable"} onClick={() => toggleDisabled(u)} data-testid={`user-toggle-${u.user_id}`}>
                              {u.disabled ? <CheckCircle2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                            </Button>
                          )}
                          {!isMe(u) && !isOwner(u) && (
                            <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive" title="Delete" onClick={() => remove(u)} data-testid={`user-delete-${u.user_id}`}><Trash2 className="w-4 h-4" /></Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>

      {/* Create */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-login-dialog">
          <DialogHeader><DialogTitle className="font-display">Add a Login</DialogTitle>
            <DialogDescription>Create a new platform login with a role and password. The user can sign in immediately.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm mb-1 block">Full Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="create-name" /></div>
            <div><Label className="text-sm mb-1 block">Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="create-email" /></div>
            <div><Label className="text-sm mb-1 block">Temporary Password</Label><Input type="text" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="min 6 characters" data-testid="create-password" /></div>
            <div><Label className="text-sm mb-1 block">Role</Label>
              <select className="border rounded-md h-10 px-3 text-sm w-full" value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="create-role">
                {CREATABLE.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            {(form.role === "society_admin" || form.role === "society_member" || form.role === "accountant") && (
              <div><Label className="text-sm mb-1 block">Society ID (optional)</Label><Input value={form.society_id} onChange={(e) => set("society_id", e.target.value)} placeholder="soc_..." data-testid="create-society" /></div>
            )}
          </div>
          <DialogFooter><Button onClick={create} disabled={busy} className="rounded-full gap-2" data-testid="create-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Create Login</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="edit-login-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit Login</DialogTitle>
            <DialogDescription>{edit?.email}</DialogDescription></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label className="text-sm mb-1 block">Full Name</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="edit-name" /></div>
              <div><Label className="text-sm mb-1 block">Role</Label>
                <select className="border rounded-md h-10 px-3 text-sm w-full" value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })} disabled={isMe(edit)} data-testid="edit-role">
                  {ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                {isMe(edit) && <p className="text-xs text-muted-foreground mt-1">You cannot change your own role.</p>}
              </div>
              <div><Label className="text-sm mb-1 block">Society ID (optional)</Label><Input value={edit.society_id} onChange={(e) => setEdit({ ...edit, society_id: e.target.value })} placeholder="soc_..." data-testid="edit-society" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} disabled={busy} className="rounded-full gap-2" data-testid="edit-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!pwUser} onOpenChange={(o) => !o && setPwUser(null)}>
        <DialogContent data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle className="font-display">Reset Password</DialogTitle>
            <DialogDescription>Set a new password for {pwUser?.email}. They will be signed out and must log in again.</DialogDescription></DialogHeader>
          <div><Label className="text-sm mb-1 block">New Password</Label><Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="min 6 characters" data-testid="reset-pw-input" /></div>
          <DialogFooter><Button onClick={resetPw} disabled={busy} className="rounded-full gap-2" data-testid="reset-pw-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Reset Password</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
